import { useCallback, useEffect, useMemo } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import Content from "./Content";
import ShiftTooltip from "../../tooltip/ShiftTooltip";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useMenuContext } from "../../../hooks/context/useMenuContext";
import { usePermissions } from "../../../hooks/usePermissions";
import { usePreferences } from "../../../../../Settings/Configuration/hooks/usePreferences";
import { SCHEDULE_PREFERENCE_KEYS } from "../../../constants/scheduleTypes";
import { palette } from "../../../../../Settings/Configuration/Schedule/Team/Settings/Shift/colors";

function EventDisplay({ event }: any) {
    const { openMenu } = useMenuContext();
    const { selectedUnassignedEvents, dispatch, type } = useScheduleContext();
    const { canAssign } = usePermissions();
    const [{ isDragging }, dragRef, previewRef] = useDrag(() => ({
        type: "UNASSIGNED_EVENT",
        item: event,
        collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
        canDrag: canAssign && !event?.locked
    }), [event, canAssign]);

    useEffect(() => {
        previewRef(getEmptyImage(), { captureDraggingState: true });
    }, [previewRef]);

    const preferences = usePreferences(SCHEDULE_PREFERENCE_KEYS[type]);
    const preferenceStyle = useMemo(() => {
        const caseDisplay = preferences?.shiftCaseDisplay || "normal";
        const shiftColors = palette.regular[preferences?.colors?.regular ?? 0];
        return {
            textTransform: caseDisplay === "normal" ? "none" : caseDisplay,
            backgroundColor: shiftColors.bg,
            borderLeftColor: shiftColors.border,
        };
    }, [preferences?.colors?.regular, preferences?.shiftCaseDisplay]);

    const checked = useMemo(() => {
        return !!selectedUnassignedEvents?.find((item: any) => item.id === event.id)
    }, [event.id, selectedUnassignedEvents]);

    const onEventChange = useCallback(() => {
        dispatch({ type: "select-unassign-event", payload: { event } })
    }, [dispatch, event]);

    const handleDoubleClick = useCallback((e: any) => {
        e.stopPropagation();
        dispatch({
            type: "selected-cell-or-event",
            payload: { isCreating: false, isUnassignedShift: true, ...event }
        });
    }, [dispatch, event]);

    const onContextMenu = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();

        openMenu({
            id: "unassigned-shifts-menu",
            x: e.clientX,
            y: e.clientY + 10,
            selected: { ...event, isUnassignedShift: true }
        });
    }, [openMenu, event]);

    return (
        <ShiftTooltip data={{ ...event, type: "unassigned" }}>
            <div
                ref={dragRef as any}
                className="tw-relative tw-mb-2 tw-rounded-md tw-px-3 tw-py-2 tw-cursor-pointer tw-flex tw-justify-between tw-items-center tw-border-l-2"
                style={{ ...preferenceStyle, opacity: isDragging ? 0.5 : 1 }}
                onContextMenu={onContextMenu}
                onDoubleClick={handleDoubleClick}
            >
                <Content
                    event={event}
                    preferenceStyle={preferenceStyle}
                    checked={checked}
                    showCheckbox={selectedUnassignedEvents?.length > 0}
                    onEventChange={onEventChange}
                />
            </div>
        </ShiftTooltip>
    );
}

export default EventDisplay;
